import { TIPO_VOTO } from '@/dominio/TipoVoto';

class PlacarVotacaoService {
    /**
     * Conta votos de uma deliberação
     */
    static contarVotosDeliberacao(deliberacao) {
        if (!deliberacao?.votos) {
            return 1; // Retorna 1 pois o autor sempre conta
        }

        const votosConvergentes = deliberacao.votos.filter(
            (v) =>
                v.tipo?.id === TIPO_VOTO.CONVERGENTE.codigo ||
                v.tipo?.id === TIPO_VOTO.CONVERGENTE_COM_RESSALVA.codigo ||
                v.tipo?.id === TIPO_VOTO.RELATOR.codigo
        );

        const votosOutrosMagistrados = votosConvergentes.filter(
            (v) => v.magistradoResponsavel !== deliberacao.magistradoResponsavel
        );

        return votosOutrosMagistrados.length + 1; // Conta os votos de outros magistrados + 1 do autor
    }

    /**
     * Calcula placar detalhado
     */
    static calcularPlacarDeliberacao(deliberacao) {
        if (!deliberacao) return null;

        const votosConvergentes =
            deliberacao.votos?.filter(
                (v) =>
                    v.tipo?.id === TIPO_VOTO.CONVERGENTE.codigo ||
                    v.tipo?.id === TIPO_VOTO.CONVERGENTE_COM_RESSALVA.codigo ||
                    v.tipo?.id === TIPO_VOTO.RELATOR.codigo
            ) || [];

        const votosOutrosMagistrados = votosConvergentes.filter(
            (v) => v.magistradoResponsavel !== deliberacao.magistradoResponsavel
        );

        return {
            deliberacaoId: deliberacao.id,
            descricao: deliberacao.descricao,
            magistradoResponsavel: deliberacao.magistradoResponsavel,
            total: votosOutrosMagistrados.length + 1,
            votosExplicitos: votosOutrosMagistrados.length,
            detalhesVotos: votosOutrosMagistrados.map((v) => v.magistradoResponsavel)
        };
    }

    /**
     * Obtém deliberação vencedora
     */
    static obterDeliberacaoVencedora(materia) {
        if (!materia?.deliberacoes?.length) return null;

        // Calcula todos os votos
        const resultados = materia.deliberacoes.map((deliberacao) => ({
            deliberacao,
            totalVotos: PlacarVotacaoService.contarVotosDeliberacao(deliberacao)
        }));

        // Ordena por total de votos (decrescente)
        resultados.sort((a, b) => b.totalVotos - a.totalVotos);

        // Se não há votos
        if (resultados[0].totalVotos === 0) {
            return null;
        }

        // Verifica se há empate no primeiro lugar
        const primeiroTotal = resultados[0].totalVotos;
        const empatadas = resultados.filter((r) => r.totalVotos === primeiroTotal);

        // Se mais de uma deliberação tem o mesmo total máximo, é empate
        return empatadas.length === 1 ? resultados[0].deliberacao : null;
    }

    /**
     * Obtém deliberações empatadas
     */
    static obterDeliberacoesEmpatadas(materia) {
        if (!materia?.deliberacoes?.length) return [];

        const resultados = materia.deliberacoes.map((deliberacao) => ({
            deliberacao,
            totalVotos: PlacarVotacaoService.contarVotosDeliberacao(deliberacao)
        }));

        resultados.sort((a, b) => b.totalVotos - a.totalVotos);

        if (resultados[0].totalVotos === 0) return [];

        const primeiroTotal = resultados[0].totalVotos;
        return resultados.filter((r) => r.totalVotos === primeiroTotal).map((r) => r.deliberacao);
    }

    /**
     * Contabiliza placar completo da matéria
     */
    static contabilizarPlacarMateria(materia, siglaRelator) {
        if (!materia?.deliberacoes?.length) return null;

        const placarTodasDeliberacoes = materia.deliberacoes.map((deliberacao) => {
            const votosConvergentes =
                deliberacao.votos?.filter(
                    (v) =>
                        v.tipo?.id === TIPO_VOTO.CONVERGENTE.codigo ||
                        v.tipo?.id === TIPO_VOTO.CONVERGENTE_COM_RESSALVA.codigo ||
                        v.tipo?.id === TIPO_VOTO.RELATOR.codigo // Inclui voto do relator
                ) || [];

            const votosOutrosMagistrados = votosConvergentes.filter(
                (v) => v.magistradoResponsavel !== deliberacao.magistradoResponsavel
            );

            const total = votosOutrosMagistrados.length + 1;

            return {
                deliberacaoId: deliberacao.id,
                descricao: deliberacao.descricao,
                magistradoResponsavel: deliberacao.magistradoResponsavel,
                total: total,
                ehDoRelator: deliberacao.magistradoResponsavel === siglaRelator,
                temVotoRelatorExplicito: deliberacao.votos?.some(
                    (v) => v.magistradoResponsavel === siglaRelator && v.tipo?.id === TIPO_VOTO.RELATOR.codigo
                )
            };
        });

        const ordenado = [...placarTodasDeliberacoes].sort((a, b) => b.total - a.total);
        const deliberacaoRelator = placarTodasDeliberacoes.find((p) => p.ehDoRelator);

        // Calcula posição do relator
        let posicaoRelator = 0;
        if (deliberacaoRelator) {
            const totalRelator = deliberacaoRelator.total;
            posicaoRelator = 1;
            for (const p of ordenado) {
                if (p.total > totalRelator) {
                    posicaoRelator++;
                } else if (p.total === totalRelator) {
                    break;
                }
            }
        }

        // Calcula empates
        const gruposPorTotal = new Map();
        ordenado.forEach((placar) => {
            if (!gruposPorTotal.has(placar.total)) {
                gruposPorTotal.set(placar.total, []);
            }
            gruposPorTotal.get(placar.total).push(placar);
        });

        const empates = [];
        gruposPorTotal.forEach((grupo, total) => {
            if (grupo.length > 1) {
                // Encontra posição do grupo
                let posicao = 1;
                for (const p of ordenado) {
                    if (p.total > total) {
                        posicao++;
                    } else if (p.total === total) {
                        break;
                    }
                }

                empates.push({
                    total,
                    deliberacoes: grupo,
                    posicao
                });
            }
        });

        // Status do relator
        let relatorStatus = null;
        if (deliberacaoRelator) {
            if (posicaoRelator === 1) {
                const grupoPrimeiro = gruposPorTotal.get(ordenado[0]?.total);
                const empatePrimeiro = grupoPrimeiro && grupoPrimeiro.length > 1;

                if (empatePrimeiro && grupoPrimeiro.some((p) => p.ehDoRelator)) {
                    relatorStatus = { tipo: 'EMPATE_PRIMEIRO', texto: 'Empate' };
                } else {
                    relatorStatus = { tipo: 'VENCENDO', texto: 'Mais votado' };
                }
            } else if (posicaoRelator > 1) {
                relatorStatus = { tipo: 'PERDENDO', texto: `${posicaoRelator}º lugar` };
            }
        }

        const resumoPlacar = ordenado.map((p) => p.total).join(' × ');

        return {
            deliberacaoRelator,
            placarTodasDeliberacoes: ordenado,
            relatorStatus,
            posicaoRelator,
            empates,
            resumoPlacar,
            temEmpate: empates.length > 0,
            temDeliberacaoRelator: !!deliberacaoRelator,
            relatorVotouExplicitamente: deliberacaoRelator?.temVotoRelatorExplicito || false
        };
    }

    /**
     * Obtém estado dos votos de um magistrado
     */
    static obterEstadoVotosMagistradoPorMateria(materia, magistradoSigla) {
        const resultados = {
            temVotoConvergente: false,
            deliberacaoComVotoConvergente: null,
            totalVotosConvergentes: 0,
            podeVotarConvergentemente: true,
            temVotoRelator: false
        };

        if (!materia?.deliberacoes) return resultados;

        materia.deliberacoes.forEach((deliberacao) => {
            const voto = deliberacao.votos?.find((v) => v.magistradoResponsavel === magistradoSigla);
            if (voto) {
                const tipoId = voto.tipo?.id;
                if (
                    tipoId === TIPO_VOTO.CONVERGENTE.codigo ||
                    tipoId === TIPO_VOTO.CONVERGENTE_COM_RESSALVA.codigo ||
                    tipoId === TIPO_VOTO.RELATOR.codigo // Inclui voto do relator
                ) {
                    resultados.temVotoConvergente = true;
                    resultados.deliberacaoComVotoConvergente = deliberacao;
                    resultados.totalVotosConvergentes++;

                    if (tipoId === TIPO_VOTO.RELATOR.codigo) {
                        resultados.temVotoRelator = true;
                    }
                }
            }
        });

        resultados.podeVotarConvergentemente = resultados.totalVotosConvergentes === 0;
        return resultados;
    }

    /**
     * Verifica se há empate
     */
    static haEmpateNaMateria(materia) {
        const vencedora = PlacarVotacaoService.obterDeliberacaoVencedora(materia);
        return !vencedora && materia.deliberacoes?.some((o) => PlacarVotacaoService.contarVotosDeliberacao(o) > 0);
    }

    /**
     * Obtém descrição do empate
     */
    static getDescricaoEmpate(grupoEmpate) {
        if (!grupoEmpate || grupoEmpate.deliberacoes.length < 2) return '';

        const deliberacoes = grupoEmpate.deliberacoes;
        const posicao = grupoEmpate.posicao;

        if (posicao === 1) {
            if (deliberacoes.length === 2) {
                return `1º lugar empatado entre "${deliberacoes[0].descricao}" e "${deliberacoes[1].descricao}"`;
            }
            const nomes = deliberacoes.map((o) => `"${o.descricao}"`).join(', ');
            return `1º lugar empatado entre ${deliberacoes.length} deliberações: ${nomes}`;
        }

        if (deliberacoes.length === 2) {
            return `${posicao}º lugar empatado entre "${deliberacoes[0].descricao}" e "${deliberacoes[1].descricao}"`;
        }

        return `${posicao}º lugar empatado entre ${deliberacoes.length} deliberações`;
    }

    /**
     * Calcula posições com empates
     */
    static calcularPosicoesComEmpates(placarTodasDeliberacoes) {
        if (!placarTodasDeliberacoes.length) return [];

        // Ordena por total (já está ordenado no placar, mas garantimos)
        const ordenado = [...placarTodasDeliberacoes].sort((a, b) => b.total - a.total);

        const posicoes = [];
        let posicaoAtual = 1;
        let i = 0;

        while (i < ordenado.length) {
            const currentTotal = ordenado[i].total;
            // Conta quantas têm o mesmo total
            let countMesmoTotal = 1;
            while (i + countMesmoTotal < ordenado.length && ordenado[i + countMesmoTotal].total === currentTotal) {
                countMesmoTotal++;
            }

            // Todas as deliberações com o mesmo total têm a mesma posição
            for (let j = 0; j < countMesmoTotal; j++) {
                posicoes.push({
                    ...ordenado[i + j],
                    posicao: posicaoAtual,
                    temEmpate: countMesmoTotal > 1
                });
            }

            // Avança para o próximo grupo
            i += countMesmoTotal;
            posicaoAtual += countMesmoTotal;
        }

        return posicoes;
    }
}

export default PlacarVotacaoService;
